import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SubscriptionDiscountDto, SubscriptionPlanDto } from '../../../core/models/admin.models';
import { BillingService } from '../../../core/services/billing.service';
import { LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './pricing.component.html',
})
export class PricingComponent implements OnInit {
  private readonly billingService = inject(BillingService);
  private readonly lang = inject(LanguageService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  plans = signal<SubscriptionPlanDto[]>([]);
  loading = signal(true);

  fieldSuffix = computed(() => {
    const l = this.lang.currentLang();
    if (l === 'ru') return 'Ru';
    if (l === 'en') return 'En';
    if (l === 'uz-cyrl') return 'Cyrl';
    return '';
  });

  localized(plan: SubscriptionPlanDto, field: 'name' | 'description'): string | null {
    const suffix = this.fieldSuffix();
    if (!suffix) return plan[field] ?? null;
    const key = (field + suffix) as keyof SubscriptionPlanDto;
    return (plan[key] as string | null) || plan[field] || null;
  }

  ngOnInit() {
    this.billingService
      .getPublicPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.plans.set(res.data ?? []);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  isFeatured(index: number, total: number): boolean {
    return total > 1 && index === Math.floor(total / 2);
  }

  formatPrice(price: number): string {
    if (price <= 0) return this.translate.instant('LANDING.PRICING.FREE');
    return new Intl.NumberFormat('uz-UZ').format(price);
  }

  formatStores(max: number): string {
    return max <= 0 || max > 100
      ? this.translate.instant('LANDING.PRICING.UNLIMITED')
      : this.translate.instant('LANDING.PRICING.STORES', { count: max });
  }

  formatEmployees(max: number): string {
    return max <= 0 || max > 100
      ? this.translate.instant('LANDING.PRICING.UNLIMITED')
      : this.translate.instant('LANDING.PRICING.EMPLOYEES', { count: max });
  }

  formatProducts(max: number): string {
    return this.translate.instant('LANDING.PRICING.PRODUCTS', {
      count: new Intl.NumberFormat('uz-UZ').format(max),
    });
  }

  activeDiscounts(plan: SubscriptionPlanDto): SubscriptionDiscountDto[] {
    return plan.discounts.filter((d) => d.isActive).sort((a, b) => a.minMonths - b.minMonths);
  }

  discountedPrice(price: number, percent: number): string {
    return this.formatPrice(Math.round(price * (1 - percent / 100)));
  }

  bestDiscount(plan: SubscriptionPlanDto): SubscriptionDiscountDto {
    return this.activeDiscounts(plan).reduce((best, d) =>
      d.discountPercent > best.discountPercent ? d : best,
    );
  }

  bestDiscountedPrice(plan: SubscriptionPlanDto): string {
    const d = this.bestDiscount(plan);
    return this.formatPrice(Math.round(plan.monthlyPrice * (1 - d.discountPercent / 100)));
  }

  discountLabel(d: SubscriptionDiscountDto): string {
    return d.minMonths === 0
      ? this.translate.instant('LANDING.PRICING.DISCOUNT_ALWAYS')
      : this.translate.instant('LANDING.PRICING.DISCOUNT_MONTHS', { count: d.minMonths });
  }

  ctaLabel(plan: SubscriptionPlanDto): string {
    return plan.monthlyPrice <= 0
      ? this.translate.instant('LANDING.PRICING.CTA_FREE')
      : this.translate.instant('LANDING.PRICING.CTA_BUY');
  }

  scrollTo(id: string) {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
